import {Option} from "../../shared/utilities/OptionT";
import {APIDao, Requester} from "../APIRequester";
import {CustomError} from "../../shared/errors/CustomErrors";
import {APP_SETTINGS_API_PREFIX} from "../../common/constants";

/** Фрагмент общих настроек для клиента (Stripe + карта). */
export type ApplicationClientContextDto = {
    stripePercentFee: number;
    stripeFixedFeeUsd: number;
    stripePaymentToleranceUsd: number;
    cardPaymentMode: "ALL" | "WHITELIST";
    cardPaymentAllowed: boolean;
};

export type ApplicationAdminSettingsDto = {
    stripePercentFee: number;
    stripeFixedFeeUsd: number;
    stripePaymentToleranceUsd: number;
    cardPaymentMode: "ALL" | "WHITELIST";
    allowedUserIds: number[];
    inactiveOrderReminderEmailSubject: string;
    inactiveOrderReminderEmailBody: string;
    inactiveOrderReminderUseCustomEmailBody?: boolean;
    inactiveOrderReminderDefaultMonthsThreshold?: number;
    inactiveOrderReminderAllowedMonthsThresholds?: number[];
    mailSmtpProtocol?: string;
    mailSmtpPort?: number;
    mailSmtpHost?: string;
    mailSmtpUsername?: string;
    mailSmtpPasswordConfigured?: boolean;
};

export type SmtpMailAdminUpdateDto = {
    protocol: string;
    port: number;
    host: string;
    username: string;
    /** Пустой — не менять сохранённый пароль. */
    password?: string;
};

export type SmtpMailTestResultDto = {
    success: boolean;
    message: string;
};

export type InactiveOrderReminderConfigAdminUpdateDto = {
    inactiveOrderReminderDefaultMonthsThreshold: number;
};

export type ApplicationPaymentAdminUpdateDto = {
    stripePercentFee: number;
    stripeFixedFeeUsd: number;
    stripePaymentToleranceUsd: number;
    cardPaymentMode: "ALL" | "WHITELIST";
    allowedUserIds: number[];
};

export type InactiveOrderReminderMailAdminUpdateDto = {
    inactiveOrderReminderEmailSubject: string;
    inactiveOrderReminderEmailBody: string;
    inactiveOrderReminderUseCustomEmailBody?: boolean;
};

export class ApplicationSettingsRepository {
    constructor(private dao: Requester) {}

    public async getClientContext(): Promise<Option<ApplicationClientContextDto>> {
        return await this.dao
            .getRequest<ApplicationClientContextDto>(`${APP_SETTINGS_API_PREFIX}/client-context`, {})
            .then((res) => {
                const result: Option<ApplicationClientContextDto> = {
                    Some: res.data,
                };
                return result;
            })
            .catch((err: CustomError) => {
                const result: Option<ApplicationClientContextDto> = {
                    Some: {} as ApplicationClientContextDto,
                    None: err,
                };
                return result;
            });
    }

    public async getAdmin(): Promise<Option<ApplicationAdminSettingsDto>> {
        return await this.dao
            .getRequest<ApplicationAdminSettingsDto>(`${APP_SETTINGS_API_PREFIX}/admin`, {})
            .then((res) => {
                const result: Option<ApplicationAdminSettingsDto> = {
                    Some: res.data,
                };
                return result;
            })
            .catch((err: CustomError) => {
                const result: Option<ApplicationAdminSettingsDto> = {
                    Some: {} as ApplicationAdminSettingsDto,
                    None: err,
                };
                return result;
            });
    }

    public async updateAdmin(body: ApplicationAdminSettingsDto): Promise<Option<ApplicationAdminSettingsDto>> {
        return await this.dao
            .putRequest<ApplicationAdminSettingsDto>(`${APP_SETTINGS_API_PREFIX}/admin`, {data: body})
            .then((res) => {
                const result: Option<ApplicationAdminSettingsDto> = {
                    Some: res.data,
                };
                return result;
            })
            .catch((err: CustomError) => {
                const result: Option<ApplicationAdminSettingsDto> = {
                    Some: {} as ApplicationAdminSettingsDto,
                    None: err,
                };
                return result;
            });
    }

    public async updatePaymentAdmin(
        body: ApplicationPaymentAdminUpdateDto,
    ): Promise<Option<ApplicationAdminSettingsDto>> {
        return await this.dao
            .putRequest<ApplicationAdminSettingsDto>(`${APP_SETTINGS_API_PREFIX}/admin/payment`, {data: body})
            .then((res) => {
                const result: Option<ApplicationAdminSettingsDto> = {
                    Some: res.data,
                };
                return result;
            })
            .catch((err: CustomError) => {
                const result: Option<ApplicationAdminSettingsDto> = {
                    Some: {} as ApplicationAdminSettingsDto,
                    None: err,
                };
                return result;
            });
    }

    public async updateInactiveOrderReminderConfig(
        body: InactiveOrderReminderConfigAdminUpdateDto,
    ): Promise<Option<ApplicationAdminSettingsDto>> {
        return await this.dao
            .putRequest<ApplicationAdminSettingsDto>(
                `${APP_SETTINGS_API_PREFIX}/admin/inactive-order-reminder-config`,
                {data: body},
            )
            .then((res) => {
                const result: Option<ApplicationAdminSettingsDto> = {
                    Some: res.data,
                };
                return result;
            })
            .catch((err: CustomError) => {
                const result: Option<ApplicationAdminSettingsDto> = {
                    Some: {} as ApplicationAdminSettingsDto,
                    None: err,
                };
                return result;
            });
    }

    public async updateInactiveOrderReminderMail(
        body: InactiveOrderReminderMailAdminUpdateDto,
    ): Promise<Option<ApplicationAdminSettingsDto>> {
        return await this.dao
            .putRequest<ApplicationAdminSettingsDto>(
                `${APP_SETTINGS_API_PREFIX}/admin/inactive-order-reminder-mail`,
                {data: body},
            )
            .then((res) => {
                const result: Option<ApplicationAdminSettingsDto> = {
                    Some: res.data,
                };
                return result;
            })
            .catch((err: CustomError) => {
                const result: Option<ApplicationAdminSettingsDto> = {
                    Some: {} as ApplicationAdminSettingsDto,
                    None: err,
                };
                return result;
            });
    }

    public async updateSmtpMail(body: SmtpMailAdminUpdateDto): Promise<Option<ApplicationAdminSettingsDto>> {
        return await this.dao
            .putRequest<ApplicationAdminSettingsDto>(`${APP_SETTINGS_API_PREFIX}/admin/smtp-mail`, {data: body})
            .then((res) => {
                const result: Option<ApplicationAdminSettingsDto> = {
                    Some: res.data,
                };
                return result;
            })
            .catch((err: CustomError) => {
                const result: Option<ApplicationAdminSettingsDto> = {
                    Some: {} as ApplicationAdminSettingsDto,
                    None: err,
                };
                return result;
            });
    }

    public async testSmtpMail(body: SmtpMailAdminUpdateDto): Promise<Option<SmtpMailTestResultDto>> {
        return await this.dao
            .postRequest<SmtpMailTestResultDto>(`${APP_SETTINGS_API_PREFIX}/admin/smtp-mail/test`, {data: body})
            .then((res) => {
                const result: Option<SmtpMailTestResultDto> = {
                    Some: res.data,
                };
                return result;
            })
            .catch((err: CustomError) => {
                const result: Option<SmtpMailTestResultDto> = {
                    Some: {success: false, message: err.getErrorDescription()},
                    None: err,
                };
                return result;
            });
    }
}

export const ApplicationSettingsRepositoryInstance = new ApplicationSettingsRepository(APIDao);
